import React, { useCallback, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";

import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";

import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";

import KMKTable from "../Common/Table/KMKTable";
import { SUMMARY_COLUMNS } from "./DQMData";
import { convertTimezone } from "../../util/common";
import { navQualityDashboard } from "../kmk/AppData";
import { ButtonUI } from "../Common";

const useStyles = makeStyles()((theme) => ({
  tableHeader: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  searchContainer: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  progressContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color: "#666666",
    fontSize: "12px",
  },
  LinearProgress: {
    height: 5,
    borderRadius: 5,
    width: "80%",
  },
  findings: {
    display: "flex",
    alignItems: "center",
    justifyContent: "end",

    "& > .MuiSvgIcon-root": {
      minWidth: "40px",
    },

    "& .MuiButtonBase-root": {
      minWidth: "fit-content",
      width: "fit-content",
      padding: 0,
      margin: 0,
    },
  },
  ruleContainer: {
    display: "flex",
    alignItems: "center",

    "& > .MuiSvgIcon-root": {
      display: "inline-block",
      marginRight: "3px",
    },

    "& > span": {
      display: "inline-block",
      lineHeight: "100%",
    },

    margin: "4px 0 4px 0",
  },
  capitalizeWords: {
    textTransform: "capitalize",
  },
  DataTableContainer: {
    borderRadius: 10,
    backgroundColor: "#FFFFFF",
    padding: "5px 0px 0px",
    overflowX: "auto",
    "& .MuiTableRow-head .MuiTableCell-root": {
      padding: "6px 16px",
      height: "37px",
    },
    "& .MuiTableRow-head .MuiTableCell-root:last-child": {
      paddingRight: "40px",
    },
    "& .MuiTableBody-root .MuiTableCell-root": {
      padding: "6px 16px",
      borderBottom: "none",
    },
    "& .MuiTableBody-root .MuiTableCell-root>.MuiIconButton-root:first-child": {
      marginLeft: "-10px",
      verticalAlign: "sub",
    },
    "& ::-webkit-scrollbar-track": {
      marginTop: "36px",
    },
  },
}));

const displaySource = (Workspace_id, sourceName) => {
  return (
    <Link
      to={{
        pathname: `/my-workspaces/${Workspace_id}/${navQualityDashboard.link}`,
      }}
      state={{ Name: sourceName ?? "" }}
      style={{ marginLeft: "4px" }}
    >
      {sourceName}
    </Link>
  );
};

const getHealthCell = (record) => {
  const { TotalCount = 0, Acceptable = 0, Critical = 0, Health = 0 } = record;
  const Good = TotalCount - Acceptable - Critical;

  const getSegmentPercentage = () => {
    if (TotalCount > 0) {
      return {
        good: (Good / TotalCount) * 100,
        acceptable: (Acceptable / TotalCount) * 100,
        critical: (Critical / TotalCount) * 100,
      };
    } else return { red: 0, yellow: 0, green: 0 };
  };

  const { good, acceptable, critical } = getSegmentPercentage();

  return (
    <Box sx={{ width: "100%", minWidth: 100 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2" mb={0.5}>
          {Health}%
        </Typography>
      </Box>
      <Box
        sx={{
          height: 5,
          width: "100%",
          borderRadius: 5,
          backgroundColor: "#e0e0e0",
          overflow: "hidden",
          display: "flex",
        }}
      >
        {good > 0 && <Box sx={{ width: `${good}%`, backgroundColor: "#1B5E20" }} />}
        {acceptable > 0 && <Box sx={{ width: `${acceptable}%`, backgroundColor: "#FBC02D" }} />}
        {critical > 0 && <Box sx={{ width: `${critical}%`, backgroundColor: "#EF5350" }} />}
      </Box>
    </Box>
  );
};

const convertToCSV = (summary) => {
  if (!summary || summary.length === 0) return "";

  const headers = SUMMARY_COLUMNS.map((col) => col.label.toUpperCase().replace(/ /g, "_")).join(",");
  const rows = summary.map((row) =>
    SUMMARY_COLUMNS.map((col) => {
      if (col.id === "Clean")
        return parseInt(row["TotalCount"] || 0) - parseInt(row["Acceptable"] || 0) - parseInt(row["Critical"] || 0);
      else if (col.id === "RecDate") return row?.["RecDate"] ? `"${convertTimezone(row["RecDate"])}"` : "";
      else if (col.id === "Health") return `${row[col.id] ?? 0}%`;
      return row[col.id] ?? "";
    }).join(",")
  );

  return [headers, ...rows].join("\n");
};

const SummaryTable = ({ Workspace_id, selectedDatasets, summary, loadingSummary }) => {
  const { classes } = useStyles();
  const workspace = useSelector((state) => state.workspace);
  const workspaceName = workspace.workspace.Name;

  const [search, setSearch] = useState("");

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("Health");

  //Filtering fields based on search or field change
  const filteredFields = React.useMemo(() => {
    const searchTerm = search.trim().toLowerCase();
    return summary.filter(({ Source }) => {
      const normalizedSource = (Source ?? "").toLowerCase();
      return normalizedSource.includes(searchTerm);
    });
  }, [search, summary]);

  const $columns = useMemo(() => {
    return (
      SUMMARY_COLUMNS?.map((column) => ({
        id: column.id,
        name: column.label.toUpperCase(),
        style: { minWidth: 180, maxWidth: 290, wordBreak: "break-word" },
        align: column.align,
        sortable: column.sortable,
        renderHeadCell: (name) => <>{name}</>,
        renderValueCell: (val, record, index) => {
          switch (column.id) {
            case "Source":
              return displaySource(Workspace_id, record?.["Source"] ?? "");
            case "Health":
              return getHealthCell(record);
            case "Clean":
              return (
                parseInt(record["TotalCount"] || 0) -
                parseInt(record["Acceptable"] || 0) -
                parseInt(record["Critical"] || 0)
              );
            case "RecDate":
              return record?.["RecDate"] ? convertTimezone(record?.["RecDate"]) : "-";
            default:
              return record?.[column.id];
          }
        },
      })) || []
    );
  }, [Workspace_id]);

  const $sortedData = useMemo(() => {
    const primaryKey = orderBy;
    const sorted = [...filteredFields].sort((a, b) => {
      const isNumeric = ["TotalCount", "Clean", "Critical", "Acceptable", "Health"].includes(primaryKey);
      const isDate = ["RecDate"].includes(primaryKey);

      let aPrimary, bPrimary;

      if (isNumeric) {
        if (primaryKey === "Clean") {
          aPrimary = parseInt(a["TotalCount"] || 0) - parseInt(a["Acceptable"] || 0) - parseInt(a["Critical"] || 0);
          bPrimary = parseInt(b["TotalCount"] || 0) - parseInt(b["Acceptable"] || 0) - parseInt(b["Critical"] || 0);
        } else {
          aPrimary = parseInt(a[primaryKey]) || 0;
          bPrimary = parseInt(b[primaryKey]) || 0;
        }
      } else if (isDate) {
        aPrimary = new Date(a[primaryKey]).getTime() || 0;
        bPrimary = new Date(b[primaryKey]).getTime() || 0;
      } else {
        aPrimary = (a[primaryKey] || "").toLowerCase();
        bPrimary = (b[primaryKey] || "").toLowerCase();
      }

      let primaryResult;
      if (isNumeric || isDate) {
        if (order === "asc") primaryResult = aPrimary - bPrimary;
        else primaryResult = bPrimary - aPrimary;
      } else {
        if (order === "asc") primaryResult = aPrimary.localeCompare(bPrimary);
        else primaryResult = bPrimary.localeCompare(aPrimary);
      }

      if (primaryResult !== 0) return primaryResult;

      // Secondary sort
      const aDataset = (a.Source || "").toLowerCase();
      const bDataset = (b.Source || "").toLowerCase();

      return order === "asc" ? aDataset.localeCompare(bDataset) : bDataset.localeCompare(aDataset);
    });

    return sorted;
  }, [order, orderBy, filteredFields]);

  const downloadCSV = useCallback(
    (filename = `${workspaceName} DQM Summary.csv`) => {
      const csv = convertToCSV($sortedData);
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });

      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    [$sortedData, workspaceName]
  );

  const handleSorting = useCallback((property) => {
    setOrderBy((prevProperty) => {
      setOrder((prevOrder) => {
        const isAsc = prevProperty === property && prevOrder === "asc";
        return isAsc ? "desc" : "asc";
      });

      return property;
    });
  }, []);

  const handleClearSearch = useCallback(() => setSearch(""), []);

  return (
    <Box
      component="main"
      sx={{
        mt: 2,
        pb: 3,
      }}
    >
      <Box className={classes.tableHeader}>
        <Box className={classes.searchContainer}>
          <TextField
            margin="normal"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search"
            variant="standard"
            InputProps={{
              startAdornment: <SearchIcon color="primary" />,
              ...(search && {
                endAdornment: (
                  <IconButton onClick={handleClearSearch} size="small">
                    <ClearIcon color="primary" />
                  </IconButton>
                ),
              }),
            }}
            sx={{ mr: "20px" }}
          />
        </Box>

        <ButtonUI
          variant="contained"
          color="secondary"
          style={{ minWidth: "150px", marginLeft: 15 }}
          onClick={() => downloadCSV()}
          disabled={$sortedData.length === 0}
        >
          Download Summary
        </ButtonUI>
      </Box>

      <Paper sx={{ width: "100%", overflow: "hidden" }} className={classes.DataTableContainer}>
        <KMKTable
          loading={loadingSummary}
          dataIdKey="DQM_RULE_TABLE"
          columns={$columns}
          data={$sortedData}
          sortingProperties={{ order, orderBy, handleSorting }}
          paginationProperties={{}}
          tableProps={{ size: "small", stickyHeader: true }}
          boldHeader
          containerHeight={500}
          noDataText={selectedDatasets.length !== 0 ? "No records found" : "No dataset selected"}
        />
      </Paper>
    </Box>
  );
};

export default SummaryTable;
